// © 2026 KaraokeClubs. All rights reserved.

import { toZonedTime } from "date-fns-tz";
import type { DayHours, OpeningHours, Venue, Locale } from "@/lib/types";

const WARSAW_TZ = "Europe/Warsaw";
const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

const EMPTY_HOURS: OpeningHours = {
  mon: null, tue: null, wed: null, thu: null, fri: null, sat: null, sun: null,
};

/**
 * Get the effective karaoke hours for a venue.
 * - Dedicated karaoke venues: karaoke_hours = opening_hours (they're always karaoke)
 * - Non-dedicated with known schedule: karaoke_hours from scraper
 * - Unknown schedule: null (we don't know when karaoke happens)
 */
export function getKaraokeHours(venue: Venue): OpeningHours | null {
  // If we have real karaoke hours with at least one non-null day, use them
  if (venue.karaoke_hours) {
    const hasAnyDay = Object.values(venue.karaoke_hours).some((d) => d !== null);
    if (hasAnyDay) return venue.karaoke_hours;
  }
  // Dedicated karaoke venues: opening hours ARE karaoke hours
  if (venue.is_dedicated_karaoke) return venue.opening_hours;
  // Unknown — we genuinely don't know when karaoke happens
  return null;
}

/** Whether karaoke is happening right now at this venue */
export function isKaraokeNow(venue: Venue): boolean {
  const hours = getKaraokeHours(venue);
  if (!hours) return false;
  return isVenueOpenNow(hours);
}

/**
 * Whether this venue has karaoke tonight — either it's open now, or it opens
 * later this evening. This is the real "where tonight" predicate.
 */
export function isKaraokeTonight(venue: Venue): boolean {
  const status = getKaraokeStatus(venue);
  if (!status) return false;
  return (
    status.state === "open" ||
    status.state === "closing_soon" ||
    (status.state === "opens_today" && status.tonight)
  );
}

/** Get karaoke status (like getVenueStatus but for karaoke hours) */
export function getKaraokeStatus(venue: Venue): VenueStatus | null {
  const hours = getKaraokeHours(venue);
  if (!hours) return null;
  return getVenueStatus(hours);
}

export function isVenueOpenNow(hours: OpeningHours): boolean {
  const now = toZonedTime(new Date(), WARSAW_TZ);
  const dayKey = DAY_KEYS[now.getDay()];
  const dayHours = hours[dayKey];

  if (!dayHours) return false;

  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const openMinutes = parseTime(dayHours.open);
  const closeMinutes = parseTime(dayHours.close);

  // Overnight hours (e.g., 18:00 - 04:00)
  if (closeMinutes < openMinutes) {
    return nowMinutes >= openMinutes || nowMinutes <= closeMinutes;
  }

  return nowMinutes >= openMinutes && nowMinutes <= closeMinutes;
}

function parseTime(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

export function getTodayHours(hours: OpeningHours): DayHours | null {
  const now = toZonedTime(new Date(), WARSAW_TZ);
  const dayKey = DAY_KEYS[now.getDay()];
  return hours[dayKey];
}

export function getTomorrowHours(hours: OpeningHours): DayHours | null {
  const now = toZonedTime(new Date(), WARSAW_TZ);
  const tomorrowIdx = (now.getDay() + 1) % 7;
  return hours[DAY_KEYS[tomorrowIdx]];
}

export function formatHours(dayHours: DayHours | null): string {
  if (!dayHours) return "Closed";
  return `${dayHours.open} – ${dayHours.close}`;
}

/** Returns e.g. "3:00 AM" from "03:00" */
function formatTimeShort(time24: string): string {
  const [h, m] = time24.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12} ${suffix}` : `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

export type VenueStatus =
  | { state: "open"; closesAt: string }
  | { state: "closing_soon"; closesAt: string; minutesLeft: number }
  | { state: "opens_today"; opensAt: string; tonight: boolean }
  | { state: "opens_tomorrow"; opensAt: string }
  | { state: "closed_today" };

/** Anything starting at or after 17:00 counts as "tonight" for our purposes. */
const TONIGHT_FROM_MINUTES = 17 * 60;

/** Get rich status for a venue — used to drive the UI display */
export function getVenueStatus(hours: OpeningHours): VenueStatus {
  const now = toZonedTime(new Date(), WARSAW_TZ);
  const dayKey = DAY_KEYS[now.getDay()];
  const dayHours = hours[dayKey];
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  if (dayHours) {
    const openMinutes = parseTime(dayHours.open);
    const closeMinutes = parseTime(dayHours.close);
    const isOvernight = closeMinutes < openMinutes;

    const isOpen = isOvernight
      ? nowMinutes >= openMinutes || nowMinutes <= closeMinutes
      : nowMinutes >= openMinutes && nowMinutes <= closeMinutes;

    if (isOpen) {
      let minutesLeft: number;
      if (isOvernight && nowMinutes >= openMinutes) {
        // Past open, closing after midnight
        minutesLeft = (24 * 60 - nowMinutes) + closeMinutes;
      } else if (isOvernight) {
        // After midnight, before close
        minutesLeft = closeMinutes - nowMinutes;
      } else {
        minutesLeft = closeMinutes - nowMinutes;
      }

      if (minutesLeft <= 60) {
        return { state: "closing_soon", closesAt: formatTimeShort(dayHours.close), minutesLeft };
      }
      return { state: "open", closesAt: formatTimeShort(dayHours.close) };
    }

    // Not open yet but has hours today
    if (nowMinutes < openMinutes) {
      return {
        state: "opens_today",
        opensAt: formatTimeShort(dayHours.open),
        tonight: openMinutes >= TONIGHT_FROM_MINUTES,
      };
    }
  }

  // Check tomorrow
  const tomorrowHours = getTomorrowHours(hours);
  if (tomorrowHours) {
    return { state: "opens_tomorrow", opensAt: formatTimeShort(tomorrowHours.open) };
  }

  return { state: "closed_today" };
}

/** Smart sort: karaoke happening now first, then opening soonest, then unknown schedule last */
export function smartSortVenues(venues: Venue[]): Venue[] {
  return [...venues].sort((a, b) => {
    const statusA = getKaraokeStatus(a);
    const statusB = getKaraokeStatus(b);

    // Venues with unknown schedule go last
    if (!statusA && !statusB) return a.name.localeCompare(b.name);
    if (!statusA) return 1;
    if (!statusB) return -1;

    const rank = (s: VenueStatus): number => {
      switch (s.state) {
        case "open": return 0;
        case "closing_soon": return 1;
        // Opening tonight beats opening earlier-in-the-day (already missed)
        case "opens_today": return s.tonight ? 2 : 5;
        case "opens_tomorrow": return 3;
        case "closed_today": return 4;
      }
    };

    const diff = rank(statusA) - rank(statusB);
    if (diff !== 0) return diff;

    // Within same state, sort by name
    return a.name.localeCompare(b.name);
  });
}

export interface HeroSummary {
  openNow: number;
  tonight: number;
  /** Earliest start time among venues opening later tonight, e.g. "8 PM" */
  earliestTonight: string | null;
  /** The next venue to open (soonest start) when nothing is open right now */
  nextVenue: { name: string; opensAt: string } | null;
}

/**
 * Compute the single "answer" to "where's karaoke tonight & when does it start?"
 * Drives the hero banner so the user gets the answer without scanning cards.
 */
export function getHeroSummary(venues: Venue[], locale: Locale): HeroSummary {
  let openNow = 0;
  let tonight = 0;
  let earliestMin = Infinity;
  let earliestTime: string | null = null;
  let nextMin = Infinity;
  let nextVenue: { name: string; opensAt: string } | null = null;

  for (const v of venues) {
    const status = getKaraokeStatus(v);
    if (!status) continue;

    if (status.state === "open" || status.state === "closing_soon") {
      openNow++;
      tonight++;
    } else if (status.state === "opens_today" && status.tonight) {
      tonight++;
      const min = parseClock(status.opensAt);
      if (min < earliestMin) { earliestMin = min; earliestTime = status.opensAt; }
      if (min < nextMin) { nextMin = min; nextVenue = { name: getVenueName(v, locale), opensAt: status.opensAt }; }
    }
  }

  return { openNow, tonight, earliestTonight: earliestTime, nextVenue };
}

/** Parse a formatted "8 PM" / "8:30 PM" back into minutes-since-midnight for comparison. */
function parseClock(label: string): number {
  const m = label.match(/(\d+)(?::(\d+))?\s*(AM|PM)/i);
  if (!m) return Infinity;
  let h = Number(m[1]) % 12;
  const min = m[2] ? Number(m[2]) : 0;
  if (/PM/i.test(m[3])) h += 12;
  return h * 60 + min;
}

export interface TimelineHour {
  /** 0-23 clock hour this bucket represents (may wrap past midnight). */
  hour: number;
  /** "21", "00" — 24h two-digit label. */
  label: string;
  /** How many venues have karaoke live during this hour. */
  count: number;
  /** True for the bucket containing the current time. */
  isNow: boolean;
}

export interface TonightTimeline {
  hours: TimelineHour[];
  /** Peak count across the strip (for bar-height normalisation). */
  peak: number;
  /** The current Warsaw hour (0-23). */
  nowHour: number;
}

/** Is karaoke live at this venue at the given absolute minutes-of-day (handles overnight). */
function isVenueLiveAtMinutes(hours: OpeningHours, dayIdx: number, minuteOfDay: number): boolean {
  // Check the target day's window, plus the previous day's overnight spill.
  const today = hours[DAY_KEYS[dayIdx]];
  if (today) {
    const open = parseTime(today.open);
    const close = parseTime(today.close);
    if (close < open) {
      if (minuteOfDay >= open) return true; // before midnight portion
    } else if (minuteOfDay >= open && minuteOfDay < close) {
      return true;
    }
  }
  const prev = hours[DAY_KEYS[(dayIdx + 6) % 7]];
  if (prev) {
    const open = parseTime(prev.open);
    const close = parseTime(prev.close);
    if (close < open && minuteOfDay < close) return true; // after-midnight spill
  }
  return false;
}

/**
 * Build the "tonight timeline": for each hour from now through 2 AM, count how
 * many venues have karaoke live. Powers the glanceable hour-ribbon that answers
 * "when does the night peak / when does it start?".
 */
export function buildTonightTimeline(venues: Venue[]): TonightTimeline {
  const now = toZonedTime(new Date(), WARSAW_TZ);
  const nowHour = now.getHours();
  const dayIdx = now.getDay();

  // Window: from the current hour until 02:00 (next day) — the karaoke night.
  const startHour = nowHour;
  const span = ((26 - startHour) % 24) || 12; // through 2 AM, min sensible span
  const hours: TimelineHour[] = [];

  for (let i = 0; i <= span; i++) {
    const abs = startHour + i;
    const clock = abs % 24;
    const crossedMidnight = abs >= 24;
    const targetDay = crossedMidnight ? (dayIdx + 1) % 7 : dayIdx;
    // Sample at :30 of the hour so a venue opening at e.g. 20:00 counts for 20.
    const minuteOfDay = clock * 60 + 30;

    let count = 0;
    for (const v of venues) {
      const h = getKaraokeHours(v);
      if (!h) continue;
      if (isVenueLiveAtMinutes(h, targetDay, minuteOfDay)) count++;
    }

    hours.push({
      hour: clock,
      label: String(clock).padStart(2, "0"),
      count,
      isNow: i === 0,
    });
    if (abs >= 26) break; // hard stop at 2 AM
  }

  const peak = hours.reduce((m, h) => Math.max(m, h.count), 0);
  return { hours, peak, nowHour };
}

/** Whether a venue's karaoke is live at a specific 24h hour (for timeline filtering). */
export function isLiveAtHour(venue: Venue, hour: number): boolean {
  const h = getKaraokeHours(venue);
  if (!h) return false;
  const now = toZonedTime(new Date(), WARSAW_TZ);
  // If the chosen hour is earlier than the current hour, it's after midnight → next day.
  const crossedMidnight = hour < now.getHours();
  const dayIdx = crossedMidnight ? (now.getDay() + 1) % 7 : now.getDay();
  return isVenueLiveAtMinutes(h, dayIdx, hour * 60 + 30);
}

/** Get time-aware greeting key for i18n */
export function getTimeGreetingKey(): string {
  const now = toZonedTime(new Date(), WARSAW_TZ);
  const hour = now.getHours();

  if (hour >= 0 && hour < 5) return "greeting_late_night";
  if (hour >= 5 && hour < 12) return "greeting_morning";
  if (hour >= 12 && hour < 17) return "greeting_afternoon";
  if (hour >= 17 && hour < 21) return "greeting_evening";
  return "greeting_night";
}

export function getVenueName(venue: Venue, locale: Locale): string {
  if (locale === "pl" && venue.name_pl) return venue.name_pl;
  if (locale === "uk" && venue.name_uk) return venue.name_uk;
  return venue.name;
}

export function getVenueDescription(venue: Venue, locale: Locale): string | null {
  if (locale === "pl" && venue.description_pl) return venue.description_pl;
  if (locale === "uk" && venue.description_uk) return venue.description_uk;
  return venue.description_en;
}

export function getScheduleNote(venue: Venue, locale: Locale): string | null {
  if (locale === "pl" && venue.karaoke_schedule_note_pl) return venue.karaoke_schedule_note_pl;
  if (locale === "uk" && venue.karaoke_schedule_note_uk) return venue.karaoke_schedule_note_uk;
  return venue.karaoke_schedule_note;
}

export function getPriceDetails(venue: Venue, locale: Locale): string | null {
  if (locale === "pl" && venue.price_details_pl) return venue.price_details_pl;
  if (locale === "uk" && venue.price_details_uk) return venue.price_details_uk;
  return venue.price_details_en;
}

export function getPriceLabel(priceRange: number | null): string {
  switch (priceRange) {
    case 1: return "$";
    case 2: return "$$";
    case 3: return "$$$";
    default: return "";
  }
}

export const DISTRICT_LABELS: Record<string, string> = {
  srodmiescie: "Śródmieście",
  wola: "Wola",
  mokotow: "Mokotów",
  praga_polnoc: "Praga Północ",
  praga_poludnie: "Praga Południe",
  zoliborz: "Żoliborz",
  ochota: "Ochota",
  ursynow: "Ursynów",
  bielany: "Bielany",
  bemowo: "Bemowo",
  targowek: "Targówek",
  rembertow: "Rembertów",
  wesola: "Wesoła",
  wawer: "Wawer",
  wilanow: "Wilanów",
  ursus: "Ursus",
  wlochy: "Włochy",
  bialoleka: "Białołęka",
};
